import React, {
    useState,
    useEffect,
    useCallback,
    useMemo,
    useRef,
    useId,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import {
    applyProjectColorCodeOptimistic,
    applyProjectIconUrlOptimistic,
    fetchProjectDetail,
    updateProject,
} from '../projects.slice'
import { getS3UrlForAttachment } from '../../taskRefactored/tasks.slice'
import { uploadAttachmentToAWS } from '../../taskRefactored/tasks.service'

import { showActionNotification } from '../../../components/notification'
import { harmonicIconToneForBackground } from '../../../helpers/harmonicIconColor'

import styles from './ProjectColorIconFlyout.module.css'

// Filled MDI icons — first 16 match the 4×4 picker grid, then 17 from the extended grid (Iconify).
const PROJECT_ICONS = [
    'mdi:format-list-bulleted',
    'mdi:poll',
    'mdi:chart-gantt',
    'mdi:calendar-blank',
    'mdi:rocket-launch',
    'mdi:account-group',
    'mdi:finance',
    'mdi:star-circle',
    'mdi:bug',
    'mdi:lightbulb',
    'mdi:earth',
    'mdi:cog',
    'mdi:notebook',
    'mdi:monitor',
    'mdi:check-circle',
    'mdi:bullseye',
    'mdi:code-tags',
    'mdi:bullhorn',
    'mdi:forum',
    'mdi:briefcase',
    'mdi:image',
    'mdi:mountain',
    'mdi:puzzle',
    'mdi:monitor-dashboard',
    'mdi:strategy',
    'mdi:speedometer',
    'mdi:medal',
    'mdi:shoe-sneaker',
    'mdi:shopping-basket',
    'mdi:map',
    'mdi:movie-open-star',
    'mdi:coins',
    'mdi:page-layout-sidebar-left',
]

/**
 * Flyout panel: color grid (palette from Create Project modal) + Icon / Upload tabs.
 */
const ProjectColorIconFlyout = ({
    projectId,
    palette,
    initialColor,
    onSaved,
}) => {
    const dispatch = useDispatch()
    const {
        projectDetail,
        clientModalProjectsListData = [],
        browseProjectsData = [],
    } = useSelector((s) => s.projectsSlice || {})
    const [tab, setTab] = useState('icon')
    const [savingColor, setSavingColor] = useState(false)
    const [savingIcon, setSavingIcon] = useState(false)
    const [uploadingImage, setUploadingImage] = useState(false)
    const [deletingUpload, setDeletingUpload] = useState(false)
    const [selectedIcon, setSelectedIcon] = useState(null)
    /** Last library icon key (Iconify) for this project — kept when switching to upload so Delete can revert. */
    const lastLibraryIconUrlRef = useRef(null)
    const fileInputRef = useRef(null)
    const uploadInputId = useId()
    const uploadBannerDescriptionId = `${uploadInputId}-banner`

    const readIconStateSnapshot = useCallback(
        (p) =>
            p?._id === projectId
                ? {
                      url: p?.url,
                      urlType: p?.urlType,
                      projectProfile: p?.projectProfile,
                  }
                : null,
        [projectId]
    )

    useEffect(() => {
        lastLibraryIconUrlRef.current = null
    }, [projectId])

    useEffect(() => {
        if (projectDetail?._id !== projectId) return
        if (projectDetail?.urlType === 'icon' && projectDetail?.url) {
            const key = String(projectDetail.url).trim()
            setSelectedIcon(key)
            lastLibraryIconUrlRef.current = key
        } else {
            setSelectedIcon(null)
        }
    }, [
        projectId,
        projectDetail?._id,
        projectDetail?.url,
        projectDetail?.urlType,
    ])

    const normalizedInitial =
        typeof initialColor === 'string' && initialColor.trim()
            ? initialColor.trim()
            : palette[0]

    /** Tile color for selected icon (live from store, then props / palette). */
    const tileProjectColor = useMemo(() => {
        const fromDetail =
            projectDetail?._id === projectId && projectDetail?.colorCode
                ? String(projectDetail.colorCode).trim()
                : ''
        if (fromDetail) return fromDetail
        return normalizedInitial
    }, [
        projectDetail?._id,
        projectId,
        projectDetail?.colorCode,
        normalizedInitial,
    ])

    const activeIconGlyphColor = useMemo(
        () => harmonicIconToneForBackground(tileProjectColor),
        [tileProjectColor]
    )

    const handleColorPick = async (hex) => {
        if (!projectId || savingColor) return
        setSavingColor(true)
        try {
            const prevColorCode =
                (projectDetail?._id === projectId
                    ? projectDetail?.colorCode
                    : null) ||
                clientModalProjectsListData.find((p) => p?._id === projectId)
                    ?.colorCode ||
                browseProjectsData.find((p) => p?._id === projectId)?.colorCode

            dispatch(
                applyProjectColorCodeOptimistic({
                    projectId,
                    colorCode: hex,
                })
            )

            const result = await dispatch(
                updateProject({
                    _id: projectId,
                    updatedData: {
                        projectColor: hex,
                        colorCode: hex,
                    },
                })
            )
            const data = result?.payload?.data
            if (data?.success) {
                onSaved?.()
            } else {
                if (prevColorCode) {
                    dispatch(
                        applyProjectColorCodeOptimistic({
                            projectId,
                            colorCode: prevColorCode,
                        })
                    )
                }
                showActionNotification({
                    type: 'error',
                    message: 'Could not update color',
                    subMessage: data?.message
                        ? String(data.message)
                        : undefined,
                    notificationKey: 'project-color-update-failed',
                })
            }
        } catch {
            showActionNotification({
                type: 'error',
                message: 'Could not update color',
                notificationKey: 'project-color-update-failed',
            })
        } finally {
            setSavingColor(false)
        }
    }

    const handleIconPick = useCallback(
        async (iconKey) => {
            if (!projectId || savingIcon) return

            const before = readIconStateSnapshot(projectDetail) ||
                readIconStateSnapshot(
                    clientModalProjectsListData.find(
                        (p) => p?._id === projectId
                    )
                ) ||
                readIconStateSnapshot(
                    browseProjectsData.find((p) => p?._id === projectId)
                ) || {
                    url: undefined,
                    urlType: undefined,
                    projectProfile: undefined,
                }

            setSavingIcon(true)
            setSelectedIcon(iconKey)

            dispatch(
                applyProjectIconUrlOptimistic({
                    projectId,
                    changes: {
                        url: iconKey,
                        urlType: 'icon',
                        projectProfile: null,
                    },
                })
            )

            try {
                const result = await dispatch(
                    updateProject({
                        _id: projectId,
                        updatedData: {
                            url: iconKey,
                            urlType: 'icon',
                            projectProfile: null,
                        },
                    })
                )
                const data = result?.payload?.data
                if (data?.success) {
                    onSaved?.()
                } else {
                    dispatch(
                        applyProjectIconUrlOptimistic({
                            projectId,
                            changes: before,
                        })
                    )
                    setSelectedIcon(
                        before?.urlType === 'icon' && before?.url
                            ? before.url
                            : null
                    )
                    showActionNotification({
                        type: 'error',
                        message: 'Could not update icon',
                        subMessage: data?.message
                            ? String(data.message)
                            : undefined,
                        notificationKey: 'project-icon-update-failed',
                    })
                }
            } catch {
                dispatch(
                    applyProjectIconUrlOptimistic({
                        projectId,
                        changes: before,
                    })
                )
                setSelectedIcon(
                    before?.urlType === 'icon' && before?.url
                        ? before.url
                        : null
                )
                showActionNotification({
                    type: 'error',
                    message: 'Could not update icon',
                    notificationKey: 'project-icon-update-failed',
                })
            } finally {
                setSavingIcon(false)
            }
        },
        [
            projectId,
            savingIcon,
            dispatch,
            projectDetail,
            clientModalProjectsListData,
            browseProjectsData,
            onSaved,
            readIconStateSnapshot,
        ]
    )

    const validateUploadFile = async (file) => {
        if (!file) return { ok: false, reason: 'No file selected' }

        const isPng = file.type === 'image/png'
        const isJpeg = file.type === 'image/jpeg' || file.type === 'image/jpg'

        if (!isPng && !isJpeg) {
            return { ok: false, reason: 'Only PNG or JPEG images are allowed.' }
        }

        const maxBytes = 5 * 1024 * 1024
        if (file.size > maxBytes) {
            return { ok: false, reason: 'Image must be under 5MB.' }
        }

        // Validate square dimensions
        const isSquare = await new Promise((resolve) => {
            const url = URL.createObjectURL(file)
            const img = new Image()
            img.onload = () => {
                const ok = img.width === img.height
                URL.revokeObjectURL(url)
                resolve(ok)
            }
            img.onerror = () => {
                URL.revokeObjectURL(url)
                resolve(false)
            }
            img.src = url
        })

        if (!isSquare) {
            return {
                ok: false,
                reason: 'Please choose a square image (same width & height).',
            }
        }

        return { ok: true }
    }

    const uploadProjectImageToS3 = useCallback(
        async (file) => {
            if (!file || !projectId) return

            const validation = await validateUploadFile(file)
            if (!validation.ok) {
                showActionNotification({
                    type: 'error',
                    message: 'Invalid image',
                    subMessage: validation.reason,
                    notificationKey: 'project-image-invalid-file',
                })
                return
            }

            const before =
                readIconStateSnapshot(projectDetail) ||
                readIconStateSnapshot(
                    clientModalProjectsListData.find(
                        (p) => p?._id === projectId
                    )
                ) ||
                readIconStateSnapshot(
                    browseProjectsData.find((p) => p?._id === projectId)
                ) || {
                    url: undefined,
                    urlType: undefined,
                    projectProfile: undefined,
                }

            if (before?.urlType === 'icon' && before?.url) {
                lastLibraryIconUrlRef.current = String(before.url).trim()
            }

            setUploadingImage(true)
            try {
                if (typeof window !== 'undefined' && !window.Buffer) {
                    window.Buffer = require('buffer').Buffer
                }

                const nameChange = file.name
                    .split('.')[0]
                    .concat(`_${dayjs().unix()}`)
                    .concat(
                        `.${file.name.split('.')[file.name.split('.').length - 1]}`
                    )

                const presignPayload = {
                    data: [
                        {
                            filename: nameChange,
                            contentType: file.type,
                        },
                    ],
                }

                const s3Result = await dispatch(
                    getS3UrlForAttachment(presignPayload)
                )
                const resBody = s3Result?.payload?.data
                if (resBody?.status !== 200) {
                    throw new Error(
                        resBody?.message || 'Could not get upload URL'
                    )
                }

                const response = resBody?.data?.data
                const uploadUrl = response?.[0]?.uploadUrl
                const publicLink = response?.[0]?.publicLink

                if (!uploadUrl || !publicLink) {
                    throw new Error('Upload URL missing')
                }

                const putResult = await uploadAttachmentToAWS({
                    url: uploadUrl,
                    data: file,
                })
                if (!putResult?.data?.ok) {
                    throw new Error('Upload to storage failed')
                }

                dispatch(
                    applyProjectIconUrlOptimistic({
                        projectId,
                        changes: {
                            url: publicLink,
                            urlType: 'upload',
                            projectProfile: null,
                        },
                    })
                )

                const result = await dispatch(
                    updateProject({
                        _id: projectId,
                        updatedData: {
                            url: publicLink,
                            urlType: 'upload',
                            projectProfile: null,
                        },
                    })
                )
                const data = result?.payload?.data
                if (data?.success) {
                    await dispatch(fetchProjectDetail({ id: projectId }))
                    showActionNotification({
                        type: 'success',
                        message: 'Project image updated',
                        notificationKey: 'project-image-updated',
                    })
                    onSaved?.()
                } else {
                    dispatch(
                        applyProjectIconUrlOptimistic({
                            projectId,
                            changes: before,
                        })
                    )
                    showActionNotification({
                        type: 'error',
                        message: data?.message || 'Upload failed',
                        notificationKey: 'project-image-upload-failed',
                    })
                }
            } catch (err) {
                dispatch(
                    applyProjectIconUrlOptimistic({
                        projectId,
                        changes: before,
                    })
                )
                showActionNotification({
                    type: 'error',
                    message:
                        err?.message === 'Upload to storage failed' ||
                        err?.message === 'Upload URL missing'
                            ? 'Upload failed. Please try again.'
                            : err?.message || 'Upload failed',
                    notificationKey: 'project-image-upload-failed',
                })
            } finally {
                setUploadingImage(false)
            }
        },
        [
            projectId,
            dispatch,
            projectDetail,
            clientModalProjectsListData,
            browseProjectsData,
            onSaved,
            readIconStateSnapshot,
        ]
    )

    const handleFileChange = useCallback(
        async (e) => {
            const file = e.target.files?.[0]
            if (e.target) e.target.value = ''
            if (!file) return
            await uploadProjectImageToS3(file)
        },
        [uploadProjectImageToS3]
    )

    const handleDeleteUploadedImage = useCallback(async () => {
        if (!projectId || deletingUpload) return

        const before =
            readIconStateSnapshot(projectDetail) ||
            readIconStateSnapshot(
                clientModalProjectsListData.find((p) => p?._id === projectId)
            ) ||
            readIconStateSnapshot(
                browseProjectsData.find((p) => p?._id === projectId)
            ) || {
                url: undefined,
                urlType: undefined,
                projectProfile: undefined,
            }

        const revertIconUrl = (
            lastLibraryIconUrlRef.current ||
            PROJECT_ICONS[0]
        ).trim()

        setDeletingUpload(true)
        dispatch(
            applyProjectIconUrlOptimistic({
                projectId,
                changes: {
                    url: revertIconUrl,
                    urlType: 'icon',
                    projectProfile: null,
                },
            })
        )
        setSelectedIcon(revertIconUrl)
        try {
            const result = await dispatch(
                updateProject({
                    _id: projectId,
                    updatedData: {
                        url: revertIconUrl,
                        urlType: 'icon',
                        projectProfile: null,
                    },
                })
            )
            const data = result?.payload?.data
            if (data?.success) {
                await dispatch(fetchProjectDetail({ id: projectId }))
                onSaved?.()
            } else {
                dispatch(
                    applyProjectIconUrlOptimistic({
                        projectId,
                        changes: before,
                    })
                )
                showActionNotification({
                    type: 'error',
                    message: data?.message || 'Could not remove image',
                    notificationKey: 'project-image-delete-failed',
                })
            }
        } catch {
            dispatch(
                applyProjectIconUrlOptimistic({
                    projectId,
                    changes: before,
                })
            )
            showActionNotification({
                type: 'error',
                message: 'Could not remove image',
                notificationKey: 'project-image-delete-failed',
            })
        } finally {
            setDeletingUpload(false)
        }
    }, [
        projectId,
        deletingUpload,
        dispatch,
        projectDetail,
        clientModalProjectsListData,
        browseProjectsData,
        onSaved,
        readIconStateSnapshot,
    ])

    const hasS3UploadPreview = useMemo(
        () =>
            projectDetail?._id === projectId &&
            projectDetail?.urlType === 'upload' &&
            Boolean(projectDetail?.url),
        [projectDetail?._id, projectId, projectDetail?.urlType, projectDetail?.url]
    )

    /** Library icons are unavailable while a custom upload is active. */
    const isUploadBlockingIcons = useMemo(
        () =>
            projectDetail?._id === projectId &&
            projectDetail?.urlType === 'upload',
        [projectDetail?._id, projectId, projectDetail?.urlType]
    )

    return (
        <div className={styles.flyoutRoot}>
            <div className={styles.flyoutSectionTitle}>Color</div>
            <div className={styles.flyoutColorGrid}>
                {palette.map((hex) => {
                    const active =
                        normalizedInitial?.toLowerCase() === hex.toLowerCase()
                    return (
                        <button
                            key={hex}
                            type="button"
                            className={`${styles.flyoutSwatch} ${
                                active ? styles.flyoutSwatchActive : ''
                            }`}
                            style={{ backgroundColor: hex }}
                            onClick={() => handleColorPick(hex)}
                            disabled={savingColor}
                            aria-label={`Select color ${hex}`}
                            title={hex}
                        >
                            {active && (
                                <Icon
                                    icon="mdi:check"
                                    width={12}
                                    height={12}
                                    className={styles.flyoutSwatchCheck}
                                />
                            )}
                        </button>
                    )
                })}
            </div>

            <div className={styles.flyoutTabRow}>
                <button
                    type="button"
                    className={`${styles.flyoutTab} ${
                        tab === 'icon' ? styles.flyoutTabActive : ''
                    }`}
                    onClick={() => setTab('icon')}
                >
                    Icon
                </button>
                <button
                    type="button"
                    className={`${styles.flyoutTab} ${
                        tab === 'upload' ? styles.flyoutTabActive : ''
                    }`}
                    onClick={() => setTab('upload')}
                >
                    Upload
                </button>
            </div>

            {tab === 'icon' && (
                <div className={styles.flyoutIconTabPanel}>
                    {isUploadBlockingIcons && (
                        <p
                            id={uploadBannerDescriptionId}
                            className={styles.flyoutIconUploadBanner}
                        >
                            Please delete the{' '}
                            <button
                                type="button"
                                className={styles.flyoutIconBannerLink}
                                onClick={() => setTab('upload')}
                            >
                                current image
                            </button>{' '}
                            before picking from the library.
                        </p>
                    )}
                    <div
                        className={`${styles.flyoutIconScroll} ${
                            isUploadBlockingIcons
                                ? styles.flyoutIconScrollDisabled
                                : ''
                        }`}
                        aria-describedby={
                            isUploadBlockingIcons
                                ? uploadBannerDescriptionId
                                : undefined
                        }
                    >
                        <div className={styles.flyoutIconGrid}>
                            {PROJECT_ICONS.map((ic) => {
                                const isSelected = selectedIcon === ic
                                const iconPickDisabled =
                                    savingIcon || isUploadBlockingIcons
                                return (
                                    <button
                                        key={ic}
                                        type="button"
                                        className={`${styles.flyoutIconBtn} ${
                                            isSelected
                                                ? styles.flyoutIconBtnActive
                                                : ''
                                        } ${
                                            iconPickDisabled
                                                ? styles.flyoutIconBtnMuted
                                                : ''
                                        }`}
                                        style={
                                            isSelected && !iconPickDisabled
                                                ? {
                                                      backgroundColor:
                                                          tileProjectColor,
                                                  }
                                                : undefined
                                        }
                                        onClick={() => handleIconPick(ic)}
                                        disabled={iconPickDisabled}
                                        aria-label={ic}
                                    >
                                        <Icon
                                            icon={ic}
                                            width={22}
                                            height={22}
                                            style={{
                                                color: iconPickDisabled
                                                    ? '#94a3b8'
                                                    : isSelected
                                                      ? activeIconGlyphColor
                                                      : '#757677',
                                            }}
                                        />
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </div>
            )}

            {tab === 'upload' && (
                <div className={styles.flyoutUpload}>
                    <input
                        id={uploadInputId}
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        className={styles.flyoutUploadInput}
                        onChange={handleFileChange}
                    />
                    {uploadingImage ? (
                        <div
                            className={styles.flyoutUploadLoading}
                            role="status"
                            aria-live="polite"
                            aria-busy="true"
                        >
                            <span className={styles.flyoutUploadLoadingText}>
                                Uploading…
                            </span>
                            <div
                                className={styles.flyoutUploadProgressTrack}
                                aria-hidden
                            >
                                <div
                                    className={styles.flyoutUploadProgressFill}
                                />
                            </div>
                        </div>
                    ) : hasS3UploadPreview ? (
                        <div className={styles.flyoutUploadPreviewWrap}>
                            <div className={styles.flyoutUploadPreviewFrame}>
                                <img
                                    src={projectDetail.url}
                                    alt=""
                                    className={styles.flyoutUploadPreviewImg}
                                />
                            </div>
                            <div className={styles.flyoutUploadActions}>
                                <button
                                    type="button"
                                    className={styles.flyoutUploadBtnSecondary}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                    disabled={deletingUpload}
                                >
                                    Upload new
                                </button>
                                <button
                                    type="button"
                                    className={styles.flyoutUploadBtnDanger}
                                    onClick={handleDeleteUploadedImage}
                                    disabled={deletingUpload}
                                >
                                    {deletingUpload ? 'Removing…' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <label
                                htmlFor={uploadInputId}
                                className={styles.flyoutUploadLabel}
                            >
                                Choose image…
                            </label>
                            <p className={styles.flyoutHint}>
                                Choose a square PNG or JPEG image under 5MB
                            </p>
                        </>
                    )}
                </div>
            )}
        </div>
    )
}

export default ProjectColorIconFlyout
